import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '../../ui';
import { SessionService } from '../../core/session/session.service';
import { Customer } from '../../core/session/session.model';
import { CustomerInformationService } from './customer-information.service';
import { BillingStatusGuardService } from '../../shared/billing-status/billing-status-guard.service';
import { buildCallViewerLink } from '../../shared/utils/call-viewer-link.util';
import { customerStatusLabel, customerStatusTone } from '../../shared/utils/customer-status.util';
import { PrintLabelDialogComponent } from './print-label-dialog.component';
import { DeleteCustomerDialogComponent } from './delete-customer-dialog.component';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HLM_FORM_FIELD,
  HlmSpinnerComponent,
  HlmSelectOption,
  HlmTooltipDirective,
} from '../../ui';

@Component({
  selector: 'cmt-customer-information',
  standalone: true,
  imports: [
    DatePipe, ReactiveFormsModule,
    HlmIconComponent, HlmBadgeComponent, HlmBtnDirective, HlmInputDirective,
    HlmSelectComponent, HlmSpinnerComponent, HlmTooltipDirective,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './customer-information.component.html',
})
export class CustomerInformationComponent implements OnInit {
  readonly session = inject(SessionService);
  private readonly service = inject(CustomerInformationService);
  private readonly dialog = inject(MatDialog);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly billingStatusGuardService = inject(BillingStatusGuardService);

  /** Whether the current user may edit the billing-status flag (EB-3820, session-stable). */
  readonly canEditBilling = this.billingStatusGuardService.canEdit();

  readonly editMode = signal(false);
  readonly saving = signal(false);
  readonly deleteInProgress = signal(false);

  readonly editDisabled = computed(() => !!this.session.customer()?.endDate);
  readonly deleteDisabled = computed(() => {
    const endDate = this.session.customer()?.endDate;
    if (!endDate) return true;
    return new Date(endDate) >= new Date(new Date().toISOString().split('T')[0]);
  });

  /**
   * Concise reason the Delete action is blocked, surfaced as an inline explainer
   * beneath the button (mirrors the User Account page). Only meaningful while
   * deleteDisabled() is true; deletion is allowed once the contract has ended.
   */
  readonly deleteDisabledReason = computed(() => {
    const endDate = this.session.customer()?.endDate;
    if (!endDate) return "The customer's contract has not ended";
    const today = new Date(new Date().toISOString().split('T')[0]);
    if (new Date(endDate) >= today) return "The customer's contract ends in the future";
    return '';
  });

  readonly form = new FormGroup({
    companyName: new FormControl('', { nonNullable: true }),
    maxDaysHistory: new FormControl(0, {
      nonNullable: true,
      validators: [Validators.min(-1), Validators.pattern(/^-?\d+$/)],
    }),
    privacyLevel: new FormControl('STANDARD', { nonNullable: true }),
    routingPriority: new FormControl(0, {
      nonNullable: true,
      validators: [Validators.min(0), Validators.max(1)],
    }),
    testUser: new FormControl(false, { nonNullable: true }),
    billingEnabled: new FormControl<boolean>(true, { nonNullable: true }),
  });

  readonly privacyOptions: HlmSelectOption<string>[] = [
    { value: 'STANDARD', label: 'Standard' },
    { value: 'HIGH', label: 'High' },
  ];

  readonly yesNoOptions: HlmSelectOption<boolean>[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  readonly routingPriorityText = computed(() => {
    const priority = this.session.customer()?.routingPriority;
    if (priority === undefined || priority === null) return '';
    if (priority === 0) return 'Normal';
    if (priority === 0.1) return 'Reduced (late payments)';
    return `Reduced manually (${(priority * 100).toFixed(0)}%)`;
  });

  readonly statusBadgeTone = computed(() => customerStatusTone(this.session.customer()?.status));

  /** Human-readable status label for the hero badge (mirrors the shell). */
  readonly statusLabel = computed(() => customerStatusLabel(this.session.customer()?.status));

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const action = params['action'];
      if (!action) return;
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
      setTimeout(() => {
        if (action === 'printLabel') this.openPrintLabel();
        else if (action === 'edit') this.startEdit();
      }, 300);
    });
  }

  startEdit(): void {
    const c = this.session.customer();
    if (!c) return;
    this.form.patchValue({
      companyName: c.companyName,
      maxDaysHistory: c.maxDaysHistory,
      privacyLevel: c.privacyLevel,
      routingPriority: c.routingPriority,
      testUser: c.testUser,
      billingEnabled: c.billingEnabled ?? true,
    });
    this.editMode.set(true);
  }

  cancelEdit(): void {
    this.form.reset();
    this.editMode.set(false);
  }

  save(): void {
    const c = this.session.customer();
    if (!c || this.form.invalid) return;
    // Belt-and-braces: only trust the control when the user is permitted to edit
    // the billing flag; otherwise keep the persisted value (EB-3820).
    const nextBilling = this.canEditBilling ? this.form.controls.billingEnabled.value : c.billingEnabled;
    this.billingStatusGuardService.confirmChange(c.billingEnabled, nextBilling).subscribe(ok => {
      if (!ok) return;
      this.persist(c, nextBilling);
    });
  }

  private persist(c: Customer, nextBilling: boolean | null): void {
    this.saving.set(true);
    const updated = {
      ...c,
      companyName: this.form.controls.companyName.value,
      maxDaysHistory: Number(this.form.controls.maxDaysHistory.value),
      privacyLevel: this.form.controls.privacyLevel.value,
      routingPriority: Number(this.form.controls.routingPriority.value),
      testUser: this.form.controls.testUser.value,
      billingEnabled: nextBilling,
    };
    this.service.saveCustomer(updated).subscribe({
      next: () => {
        this.session.reloadCustomer();
        this.editMode.set(false);
        this.saving.set(false);
        this.toast.open('Customer saved', 'OK', { duration: 3000 });
      },
      error: () => {
        this.saving.set(false);
        this.toast.open('Failed to save customer', 'OK', { duration: 5000 });
      },
    });
  }

  /** Open the Call Viewer with this customer pre-filtered (EB-3811). */
  openCallViewer(): void {
    const customerId = this.session.customerId();
    if (customerId === null) {
      this.toast.open('Cannot open Call Viewer: missing scope', 'OK', { duration: 3000, tone: 'error' });
      return;
    }
    const link = buildCallViewerLink('customer', { customerId });
    this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  openPrintLabel(): void {
    const customerId = this.session.customerId();
    const brandId = this.session.brandId();
    if (customerId === null) return;
    this.dialog.open(PrintLabelDialogComponent, {
      data: { customerId, brandId },
      width: '520px',
      panelClass: 'cmt-hlm-dialog',
    });
  }

  openDeleteCustomer(): void {
    const customerId = this.session.customerId();
    if (customerId === null) return;
    this.dialog.open(DeleteCustomerDialogComponent, {
      data: { customerId },
      width: '460px',
      panelClass: 'cmt-hlm-dialog',
    });
  }
}
